import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

export default function SuperMetricsPage() {
  const q = useQuery({
    queryKey: ['super-metrics'],
    queryFn: async () => {
      const r = await api('/api/v1/super/request-metrics?hours=24');
      if (r.status !== 200) throw new Error('metrics');
      return r.data.buckets ?? [];
    },
    refetchInterval: 30_000,
  });

  const buckets = q.data ?? [];
  const total = buckets.reduce((s, b) => s + b.count, 0);
  const err5 = buckets.reduce((s, b) => s + b.status5xx, 0);
  const err4 = buckets.reduce((s, b) => s + b.status4xx, 0);
  const avgLat =
    total > 0
      ? Math.round(buckets.reduce((s, b) => s + b.latencySumMs, 0) / total)
      : 0;

  const topPaths = useMemo(() => {
    const pathTotals = new Map();
    for (const b of buckets) {
      for (const [p, n] of Object.entries(b.paths ?? {})) {
        pathTotals.set(p, (pathTotals.get(p) ?? 0) + n);
      }
    }
    return [...pathTotals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20);
  }, [buckets]);

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Request metrics"
          description="Per-minute aggregates for the last 24 hours."
          icon="BarChart3"
          metrics={[
            { label: 'Requests', value: total },
            { label: '4xx', value: err4 },
            { label: '5xx', value: err5 },
            { label: 'Avg latency ms', value: avgLat },
          ]}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Top paths</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <th className="pb-2">Path</th>
                    <th className="pb-2 text-right">Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {topPaths.map(([p, n]) => (
                    <tr key={p}>
                      <td className="py-2 font-mono text-xs">{p}</td>
                      <td className="py-2 text-right font-mono data-text">{n}</td>
                    </tr>
                  ))}
                  {!topPaths.length && (
                    <tr>
                      <td colSpan={2} className="py-4 text-muted-foreground">
                        No traffic yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent minutes</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <th className="pb-2">Minute</th>
                    <th className="pb-2">Company</th>
                    <th className="pb-2 text-right">N</th>
                    <th className="pb-2 text-right">5xx</th>
                    <th className="pb-2 text-right">Max ms</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[...buckets]
                    .reverse()
                    .slice(0, 30)
                    .map((b) => (
                      <tr key={b.id}>
                        <td className="py-2 font-mono text-xs">
                          {new Date(b.minute).toLocaleTimeString()}
                        </td>
                        <td className="py-2 font-mono text-xs">
                          {b.companyId.slice(0, 10)}
                        </td>
                        <td className="py-2 text-right font-mono data-text">{b.count}</td>
                        <td className="py-2 text-right font-mono data-text">{b.status5xx}</td>
                        <td className="py-2 text-right font-mono data-text">
                          {b.latencyMaxMs}
                        </td>
                      </tr>
                    ))}
                  {!buckets.length && (
                    <tr>
                      <td colSpan={5} className="py-4 text-muted-foreground">
                        No metrics yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>
);
}
